/* LabAggregateNudgeCard.jsx — iter692
 *
 * Smart Lab Upload Nudge: чете /api/lab-twin/aggregate и показва на потребителя:
 *   • Progress bar с lab data completeness (0-100%)
 *   • Списък с липсващи източници (DUTCH, hormones, metabolic panel, etc.)
 *   • Директен upload бутон → POST /api/lab-twin/pdf-import
 *
 * Скрива се автоматично ако completeness_pct >= 90 (потребителят е "пълен").
 * Best-effort: ако endpoint-ът не отговори, картата мълчи.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FlaskConical, Loader2, CheckCircle2 } from "lucide-react";

// Human-readable BG labels за всеки източник
const SOURCE_LABELS = {
  core_labs:           "Стандартни лаборатории (HbA1c, липиди, B12, D)",
  dutch:               "DUTCH хормонален панел",
  genomic:             "ДНК / SNP анализ (MTHFR, COMT)",
  thyroid:             "Щитовидна жлеза (TSH, FT4, FT3, anti-TPO)",
  metabolic:           "Метаболитен панел (инсулин, ApoB)",
  liver_kidney:        "Черен дроб и бъбреци (AST/ALT, eGFR)",
  hormones:            "Серумни хормони (E2, Прог, Т, AMH, кортизол)",
  medical_images:      "Медицински изображения (мамо, DXA)",
  legacy_profile_labs: "Лабораторни данни от профила",
};

const HIDE_THRESHOLD_PCT = 90;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabAggregateNudgeCard({ isDark, bg }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef            = useRef(null);

  const fetchAggregate = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/lab-twin/aggregate`);
      setData(res);
    } catch (e) {
      // silent — best effort (401 if not auth'd, 5xx if backend down)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAggregate(); }, [fetchAggregate]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(bg ? "Файлът трябва да е под 10 MB" : "File must be under 10 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: out } = await axios.post(`${API}/lab-twin/pdf-import`, fd);
      const found = out?.fields_count || 0;
      if (found > 0) {
        toast.success(bg
          ? `Извлечени ${found} лабораторни стойности · близнаците се обновяват`
          : `${found} lab values extracted · twins refreshing`);
        await fetchAggregate();
      } else {
        toast.warning(bg
          ? "Не успяхме да извлечем стойности от този файл"
          : "Could not extract values from this file");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || (bg ? "Грешка при качване на файла" : "Upload error"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Hide states
  if (loading) return null;
  if (!data?.summary) return null;
  const { completeness_pct, marker_coverage_pct, missing_sources, total_markers, medical_images_count } = data.summary;
  if (completeness_pct >= HIDE_THRESHOLD_PCT) return null;

  // Filter out medical_images from "missing" if user actually has scans
  const realMissing = (missing_sources || []).filter(
    s => !(s === "medical_images" && medical_images_count > 0)
  );

  return (
    <Card
      data-testid="lab-aggregate-nudge-card"
      className={`${isDark ? "bg-card border-border" : "bg-white border-slate-200"} rounded-2xl p-5 shadow-sm`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? "bg-cyan-900/30" : "bg-cyan-50"}`}>
          <FlaskConical className={`w-5 h-5 ${isDark ? "text-cyan-300" : "text-cyan-600"}`} />
        </div>
        <div className="flex-1">
          <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {bg ? "Допълнете лабораторния си профил" : "Complete your lab profile"}
          </h3>
          <p className={`text-sm mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg
              ? `Lab Twin покрива ${completeness_pct}% от източниците · ${total_markers} активни маркера`
              : `Lab Twin covers ${completeness_pct}% of sources · ${total_markers} active markers`}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 transition-all"
            style={{ width: `${completeness_pct}%` }}
            data-testid="lab-completeness-bar"
          />
        </div>
        <div className={`flex justify-between mt-1.5 text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>
          <span>{bg ? "Покритие на източници" : "Source coverage"}</span>
          <span className="font-mono">{completeness_pct}% · {marker_coverage_pct}% маркери</span>
        </div>
      </div>

      {/* Missing sources list */}
      {realMissing.length > 0 && (
        <div className={`rounded-lg p-3 mb-4 ${isDark ? "bg-slate-800/50" : "bg-slate-50"}`}>
          <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Липсват" : "Missing"}
          </p>
          <ul className="space-y-1.5">
            {realMissing.slice(0, 5).map((src) => (
              <li
                key={src}
                className={`flex items-center gap-2 text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}
                data-testid={`missing-source-${src}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-amber-400" : "bg-amber-500"}`} />
                {SOURCE_LABELS[src] || src}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Why this matters */}
      <p className={`text-xs leading-relaxed mb-4 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
        {bg
          ? "Качете PDF от лабораторно изследване — AI автоматично извлича стойности и обновява всички 20 близнака. Поддържат се български и международни формати."
          : "Upload a lab PDF — AI extracts values automatically and refreshes all 20 twins. Bulgarian and international formats supported."}
      </p>

      {/* Upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="lab-pdf-upload-input"
      />
      <Button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        data-testid="lab-pdf-upload-btn"
        className="w-full bg-gradient-to-r from-cyan-500 to-indigo-500 text-white h-11 rounded-xl font-semibold shadow-sm hover:shadow-md transition-shadow"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {bg ? "Обработваме PDF…" : "Processing PDF…"}
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            {bg ? "Качи лабораторен PDF" : "Upload lab PDF"}
          </>
        )}
      </Button>

      {completeness_pct >= 70 && (
        <p className={`flex items-center justify-center gap-1.5 text-xs mt-3 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
          <CheckCircle2 className="w-3.5 h-3.5" />
          {bg
            ? `Близо сте до пълно покритие — още ${100 - completeness_pct}% за максимална точност`
            : `Almost full coverage — ${100 - completeness_pct}% to go for maximum accuracy`}
        </p>
      )}
    </Card>
  );
}
